import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'USER' | 'BUYER' | 'SELLER' | 'ADMIN';
  requireApproved?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requireApproved = false,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requiredRole) {
    // Admin can access everything
    if (user.role !== 'ADMIN' && user.role !== requiredRole) {
      return (
        <div className="min-h-screen bg-surface-50 flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 mb-4">
              <svg
                className="w-full h-full text-danger-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-surface-900 mb-2">
              Acceso Denegado
            </h2>
            <p className="text-surface-600 mb-4">
              No tienes permisos para acceder a esta página.
            </p>
            <button
              onClick={() => window.history.back()}
              className="btn-primary"
            >
              Volver
            </button>
          </div>
        </div>
      );
    }
  }

  // Check if seller needs to be approved
  if (requireApproved && user.role === 'SELLER' && user.seller?.status !== 'APPROVED') {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="mx-auto w-16 h-16 mb-4">
            <svg
              className="w-full h-full text-gold-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-surface-900 mb-2">
            Cuenta Pendiente de Aprobación
          </h2>
          <p className="text-surface-600 mb-6">
            Tu cuenta de vendedor está siendo revisada por nuestro equipo. 
            Te notificaremos por email cuando sea aprobada.
          </p>
          <div className="bg-gold-50 border border-gold-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-gold-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-gold-800">
                  Proceso de verificación
                </h3>
                <div className="mt-2 text-sm text-gold-700">
                  <p>
                    Estamos verificando tu información para garantizar la seguridad 
                    de todos nuestros usuarios. Este proceso puede tomar hasta 24-48 horas.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.history.back()}
              className="btn-secondary"
            >
              Volver
            </button>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="btn-primary"
            >
              Ir al Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;